import type {
    ActivityGroup,
    BashGroup,
    EditGroup,
    FileChangeGroup,
    GroupWithMeta,
    OrchestrationGroup,
    ThinkingGroup,
    ToolGroup,
    UnknownGroup,
    WriteGroup,
} from "../events/messageGroups"
import { classifyBashCommand } from "./renderers/classifyBashCommand"

export type ActivityDigestSection = "changes" | "checks" | "commands" | "other"

export type ActivityChangeGroup = EditGroup | WriteGroup | FileChangeGroup
export type ActivityDigestGroup = ActivityChangeGroup | BashGroup | OrchestrationGroup | ThinkingGroup | ToolGroup | UnknownGroup | ActivityGroup

export type ActivityDigestItem = GroupWithMeta & { group: ActivityDigestGroup }

export interface ActivityDigestSummary {
    filesTouched: number
    checks: number
    commands: number
    other: number
    failures: number
    isPending: boolean
}

export interface ActivityDigestModel {
    items: ActivityDigestItem[]
    summary: ActivityDigestSummary
    latestItem?: ActivityDigestItem
    currentItem?: ActivityDigestItem
}

export function isActivityDigestItem(item: GroupWithMeta): item is ActivityDigestItem {
    switch (item.group.type) {
        case "edit":
        case "write":
        case "fileChange":
        case "bash":
        case "thinking":
        case "unknown":
        case "activity":
            return true
        case "orchestration":
        case "tool":
            return item.mode !== "row"
        default:
            return false
    }
}

export function activityDigestSection(item: ActivityDigestItem): ActivityDigestSection {
    switch (item.group.type) {
        case "edit":
        case "write":
        case "fileChange":
            return "changes"
        case "bash":
            return classifyBashCommand(item.group.command).semanticType === "verify" ? "checks" : "commands"
        case "orchestration":
            return item.group.calls.length > 0 && item.group.calls.every((call) => call.toolName === "exec_command") ? "commands" : "other"
        case "tool":
        case "thinking":
        case "unknown":
        case "activity":
            return "other"
    }
}

export function buildActivityDigest(items: ActivityDigestItem[]): ActivityDigestModel {
    const changedFilePaths = new Set<string>()
    let checks = 0
    let commands = 0
    let other = 0
    let failures = 0
    let currentItem: ActivityDigestItem | undefined

    for (const item of items) {
        if (activityHasError(item)) failures += 1
        if (activityIsPending(item)) currentItem = item

        switch (activityDigestSection(item)) {
            case "changes":
                changedFilePaths.add(activityFilePath(item))
                break
            case "checks":
                checks += 1
                break
            case "commands":
                commands += activityCommandCount(item)
                break
            case "other":
                other += activityOtherCount(item)
                break
        }
    }

    return {
        items,
        summary: {
            filesTouched: changedFilePaths.size,
            checks,
            commands,
            other,
            failures,
            isPending: currentItem !== undefined,
        },
        latestItem: items.at(-1),
        currentItem,
    }
}

function activityFilePath(item: ActivityDigestItem): string {
    switch (item.group.type) {
        case "edit":
        case "write":
        case "fileChange":
            return item.group.filePath
        default:
            return ""
    }
}

function activityHasError(item: ActivityDigestItem): boolean {
    return "isError" in item.group && item.group.isError
}

function activityIsPending(item: ActivityDigestItem): boolean {
    return "isPending" in item.group && item.group.isPending
}

function activityCommandCount(item: ActivityDigestItem): number {
    if (item.group.type !== "orchestration") return 1
    return Math.max(item.group.calls.length, 1)
}

function activityOtherCount(item: ActivityDigestItem): number {
    if (item.group.type === "activity") {
        return item.group.events.reduce((count, event) => count + (event.count ?? 1), 0)
    }
    if (item.group.type === "orchestration") return Math.max(item.group.calls.length, 1)
    return 1
}
